import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { useTranslations } from 'next-intl';
import { useClickAway, useIsomorphicLayoutEffect } from 'react-use';

import {
  ActionMenuItems,
  type TextMenuItemClick,
  type TextMenuItemLink,
  type ElementMenuItemClick,
  type ElementMenuItemLink,
} from './ActionMenuItems';
import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { useUserAgent } from '@/modules/device/useUserAgent';
import { INITIAL_PLACEMENT, getElementPlacement } from '@/modules/dom/helpers';

type ActionMenuProps = {
  menuItemType?: 'block' | 'minimal';
  desktopOnly?: boolean;
  actionMenuId: string;
  trigger: (args: {
    toggleActionMenu: () => void;
    showActionMenu: boolean;
  }) => React.ReactNode;
  menuItems: Array<
    | TextMenuItemClick
    | TextMenuItemLink
    | ElementMenuItemClick
    | ElementMenuItemLink
  >;
  elementRef: React.RefObject<HTMLElement | null>;
  scrollRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
};

export const SCROLL_DEBOUNCE_MS = 16;

export type ActionMenuHandle = {
  close: () => void;
};

export const ActionMenu = forwardRef<ActionMenuHandle, ActionMenuProps>(
  function ActionMenu(
    {
      desktopOnly = false,
      actionMenuId,
      menuItemType = 'minimal',
      trigger,
      menuItems,
      elementRef,
      scrollRef,
      className,
      onOpenChange,
    },
    ref
  ) {
    const actionMenuRef = useRef(null);
    const clickAwayRef = useRef(null);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [placement, setPlacement] = useState(INITIAL_PLACEMENT);
    const { isMobileDevice } = useUserAgent();
    const t = useTranslations('common');

    const showMobileActionMenu =
      !desktopOnly && isMobileDevice && showActionMenu;
    const showDesktopActionMenu =
      (!isMobileDevice || desktopOnly) && showActionMenu;

    function openActionMenu(e?: Event) {
      e?.preventDefault();
      e?.stopPropagation();
      setShowActionMenu(true);
      onOpenChange && onOpenChange(true);
    }

    function closeActionMenu() {
      setShowActionMenu(false);
      onOpenChange && onOpenChange(false);
    }

    function toggleActionMenu(e?: Event) {
      if (showActionMenu) {
        e?.preventDefault();
        e?.stopPropagation();
        closeActionMenu();
      } else {
        openActionMenu(e);
      }
    }

    function handleClick(
      handleActionItemClick: (id: string, key: string) => void,
      key: string
    ) {
      handleActionItemClick(actionMenuId, key);
      closeActionMenu();
    }

    function setUpdatedPlacement() {
      const updatedPlacement = getElementPlacement(
        elementRef,
        actionMenuRef,
        'bottomLeft'
      );
      setPlacement(updatedPlacement);
    }

    useImperativeHandle(
      ref,
      () => ({
        close: closeActionMenu,
      }),
      [onOpenChange]
    );

    useClickAway(clickAwayRef, (e) => {
      if (
        showDesktopActionMenu &&
        elementRef?.current?.contains(e.target as Node)
      ) {
        return;
      }
      closeActionMenu();
    });

    // Position before the browser paints, otherwise the menu flashes at
    // the viewport's top-left (INITIAL_PLACEMENT) for a frame
    useIsomorphicLayoutEffect(() => {
      if (showActionMenu) {
        setUpdatedPlacement();
      } else {
        setPlacement(INITIAL_PLACEMENT);
      }
    }, [showActionMenu, elementRef]);

    useEffect(() => {
      function handleKeydown(e: KeyboardEvent) {
        // If the enter key is pressed then open the action menu
        if (e.code === 'Enter' && !showActionMenu) {
          e.preventDefault();
          openActionMenu();
        }
        if (e.code === 'Escape' && showActionMenu) {
          e.preventDefault();
          closeActionMenu();
        }
      }

      if (elementRef && elementRef.current) {
        elementRef.current.addEventListener('keydown', handleKeydown);
      }
      return () => {
        if (elementRef && elementRef.current) {
          elementRef.current.removeEventListener('keydown', handleKeydown);
        }
      };
    }, [showActionMenu, elementRef]);

    useEffect(() => {
      // TODO: use closeActionMenu() instead of setShowActionMenu(false) to notify onOpenChange
      const handleScroll = debounce(() => {
        if (showActionMenu && document.activeElement) {
          (document.activeElement as HTMLElement).blur();
          setShowActionMenu(false);
        }
      }, SCROLL_DEBOUNCE_MS);

      const handleResize = debounce(() => {
        if (showActionMenu && document.activeElement) {
          setUpdatedPlacement();
        }
      }, SCROLL_DEBOUNCE_MS);

      if (scrollRef && scrollRef.current) {
        scrollRef.current.addEventListener('scroll', handleScroll);
      }

      window.addEventListener('resize', handleResize);

      return () => {
        if (scrollRef && scrollRef.current) {
          scrollRef.current.removeEventListener('scroll', handleScroll);
        }
        window.removeEventListener('resize', handleResize);
      };
    }, [scrollRef, showActionMenu]);

    return (
      <div className={className}>
        {trigger({
          toggleActionMenu,
          showActionMenu,
        })}
        {showDesktopActionMenu && (
          <div ref={clickAwayRef}>
            <div
              className={clsx(styles.wrapper, styles.actionMenuItemStyle, {
                [styles.actionMenuItemStyleMinimal]: menuItemType === 'minimal',
                [styles.actionMenuItemStyleBlock]: menuItemType === 'block',
              })}
              style={{ left: placement.x + 'px', top: placement.y + 'px' }}
              id={actionMenuId}
              ref={actionMenuRef}
              role="menu"
            >
              <ActionMenuItems
                menuItems={menuItems}
                closeActionMenu={closeActionMenu}
                handleClick={handleClick}
              />
            </div>
          </div>
        )}
        {showMobileActionMenu && (
          <div className={styles.mobileOverlay}>
            <div ref={clickAwayRef}>
              <div
                className={clsx(
                  styles.mobileWrapper,
                  styles.actionMenuItemStyle,
                  {
                    [styles.actionMenuItemStyleMinimal]:
                      menuItemType === 'minimal',
                    [styles.actionMenuItemStyleBlock]: menuItemType === 'block',
                  }
                )}
                style={{ left: placement.x + 'px', top: placement.y + 'px' }}
                id={actionMenuId}
                ref={actionMenuRef}
                role="menu"
              >
                <ActionMenuItems
                  menuItems={menuItems}
                  closeActionMenu={closeActionMenu}
                  handleClick={handleClick}
                />
                <ButtonMinimal onClick={() => closeActionMenu()}>
                  <Caption variant="lg-regular" colour="base" as="span">
                    {t('Cancel')}
                  </Caption>
                </ButtonMinimal>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
